import { TZDateMini } from '@date-fns/tz';
import type { RequireAtLeastOne } from 'type-fest';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import type { DateRangeSelectorValueType } from './types.js';
import type { TimeframeV2, TimeValue } from '../../core/types/time.js';
import { parseValue } from '../forms-core/time/timeParser.js';

/**
 * Compares two given timevalue objects with each other
 */
export function isEqualTimeValue(
  tv1: TimeValue | string | null,
  tv2: TimeValue | string | null,
) {
  // ref. equality check, same ref => same value
  if (tv1 === tv2) {
    return true;
  }

  if (typeof tv1 === 'string' || typeof tv2 === 'string') {
    return false;
  }

  // if only one of the objects is null, they can't be the same
  if (tv1 === null || tv2 === null) {
    return false;
  }

  // if the type differs, they must be different
  if (tv1?.type !== tv2?.type) {
    return false;
  }

  // compare by expression or iso value
  return tv1?.value === tv2?.value;
}

/**
 * Compares two given RangeSelection objects with each other
 */
export function isEqualRangeSelection(
  tf1: DateRangeSelectorValueType,
  tf2: DateRangeSelectorValueType,
) {
  // ref. equality check
  if (tf1 === tf2) {
    return true;
  }

  // if only one of the objects is null, they can't be the same
  if (tf1 === null || tf2 === null) {
    return false;
  }

  return (
    isEqualTimeValue(tf1.from, tf2.from) && isEqualTimeValue(tf1.to, tf2.to)
  );
}

/**
 * Removes the specified precision from a ISOString.
 * @internal
 */
export function removePrecision(
  isoString: string,
  precision: 'day' | 'minutes' | 'seconds' | 'milliseconds' = 'minutes',
): string {
  const relativeDate = TZDateMini.tz(getTimezone()).getTime();
  const timeValue = parseTimeAsTimeValue(isoString, relativeDate, precision);

  if (!isNaN(new TZDateMini(isoString).getTime())) {
    return timeValue ? timeValue.absoluteDate : isoString;
  }
  return isoString;
}

/**
 * Removes the specified precision from a TimeframeV2 or returns the input unchanged.
 * @internal
 */
export const removePrecisionFromTimeFrameV2 = (
  date:
    | RequireAtLeastOne<{ from: string; to: string }>
    | RequireAtLeastOne<TimeframeV2>
    | null
    | undefined,
  precision: 'day' | 'minutes' | 'seconds' | 'milliseconds' = 'minutes',
):
  | RequireAtLeastOne<{ from: string; to: string }>
  | RequireAtLeastOne<TimeframeV2>
  | null
  | undefined => {
  if (!date) {
    return date;
  }

  const { from, to } = parseValue(date);
  const relativeDate = TZDateMini.tz(getTimezone()).getTime();

  const parsedFrom = parseTimeAsTimeValue(from?.value, relativeDate, precision);
  const parsedTo = parseTimeAsTimeValue(to?.value, relativeDate, precision);

  if (parsedFrom !== null && parsedTo !== null) {
    return {
      from: parsedFrom,
      to: parsedTo,
    };
  }

  return date;
};

/**
 * Removes the specified precision from a TimeValue or returns the input unchanged.
 * @internal
 */
export const removePrecisionFromTimeValue = (
  date: TimeValue | null,
  precision: 'day' | 'minutes' | 'seconds' | 'milliseconds' = 'minutes',
): TimeValue | null => {
  if (!date) {
    return date;
  }

  const relativeDate = TZDateMini.tz(getTimezone()).getTime();
  if (date.value) {
    return parseTimeAsTimeValue(date.value, relativeDate, precision);
  }

  return date;
};
